import React, { useState } from 'react';
import {
  Clock,
  Users,
  Star,
  Award,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Bookmark
} from 'lucide-react';
import { Helmet } from 'react-helmet';

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: { name: string; avatar: string; bio: string };
  duration: string;
  lessons: number;
  students: number;
  rating: number;
  reviews: number;
  price: number;
  originalPrice?: number;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  category: string;
  image: string;
  featured: boolean;
  tags: string[];
  chapters: Chapter[];
}

interface Chapter {
  id: string;
  title: string;
  pages: Page[];
}

interface Page {
  id: string;
  content: string;
  type: 'text' | 'image' | 'video' | 'quiz';
  title?: string;
}

interface CoursesPageProps {
  onNavigateHome?: () => void;
}

const CoursesPage: React.FC<CoursesPageProps> = () => {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showBookReader, setShowBookReader] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const categories = ['All', 'AI Trading', 'Technical Analysis', 'Risk Management', 'Options Trading', 'Portfolio Management'];

  const courses: Course[] = [
    {
      id: '1',
      title: 'AI-Powered Stock Market Mastery',
      description: 'Master the art of AI-driven stock market analysis and predictions. Learn how to leverage machine learning algorithms for better trading decisions.',
      instructor: { name: 'Dr. Rajesh Mehta', avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop', bio: 'Senior AI Researcher with 15+ years in financial technology' },
      duration: '8 hours', lessons: 24, students: 1250, rating: 4.9, reviews: 342, price: 4999, originalPrice: 7999,
      level: 'Intermediate', category: 'AI Trading',
      image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
      featured: true, tags: ['AI', 'Machine Learning', 'Predictions', 'Algorithms'],
      chapters: [
        {
          id: 'ch1', title: 'Introduction to AI in Trading',
          pages: [
            { id: 'p1', type: 'text', title: 'Welcome to AI Trading', content: `<h1>Welcome to AI-Powered Stock Market Mastery</h1><p>In this comprehensive course, you'll discover how artificial intelligence is revolutionizing the world of stock market trading and investment.</p><h2>What You'll Learn</h2><ul><li>Fundamentals of AI in financial markets</li><li>Machine learning algorithms for market prediction</li><li>Building and training your own trading models</li><li>Risk management with AI tools</li></ul>` },
            { id: 'p2', type: 'text', title: 'The Evolution of Trading', content: `<h1>The Evolution of Trading Technology</h1><p>Trading has evolved dramatically over the past few decades, from manual floor trading to algorithmic and AI-powered systems.</p><h2>Key Milestones</h2><ul><li><strong>1970s:</strong> Electronic trading systems emerge</li><li><strong>2000s:</strong> Algorithmic trading becomes mainstream</li><li><strong>2020s:</strong> AI and machine learning integration</li></ul>` },
          ]
        },
        {
          id: 'ch2', title: 'Machine Learning Fundamentals',
          pages: [
            { id: 'p5', type: 'text', title: 'Understanding Machine Learning', content: `<h1>Machine Learning in Finance</h1><p>Machine learning enables computers to learn and improve from experience without being explicitly programmed.</p><h2>Types of Machine Learning</h2><ul><li><strong>Supervised Learning:</strong> Learning with labeled data</li><li><strong>Unsupervised Learning:</strong> Finding patterns in unlabeled data</li><li><strong>Reinforcement Learning:</strong> Learning through trial and error</li></ul>` },
          ]
        }
      ]
    },
    {
      id: '2',
      title: 'Technical Analysis 1:1 Session',
      description: 'Comprehensive guide to technical analysis, chart patterns, and trading indicators for the Indian stock market.',
      instructor: { name: 'Priya Sharma', avatar: 'https://images.pexels.com/photos/3785079/pexels-photo-3785079.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop', bio: 'Technical Analyst with expertise in Indian equity markets' },
      duration: '12 hours', lessons: 36, students: 2100, rating: 4.8, reviews: 567, price: 3999, originalPrice: 5999,
      level: 'Beginner', category: 'Technical Analysis',
      image: 'https://images.pexels.com/photos/6802049/pexels-photo-6802049.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
      featured: false, tags: ['Charts', 'Patterns', 'Indicators', 'Support Resistance'],
      chapters: [{ id: 'ch1', title: 'Chart Basics', pages: [{ id: 'p1', type: 'text', title: 'Introduction to Charts', content: `<h1>Understanding Stock Charts</h1><p>Charts are the foundation of technical analysis.</p><h2>Types of Charts</h2><ul><li><strong>Line Charts:</strong> Simple price movement over time</li><li><strong>Candlestick Charts:</strong> Most popular for pattern recognition</li></ul>` }] }]
    },
    {
      id: '3',
      title: 'Options Trading Strategies',
      description: 'Learn advanced options trading strategies, Greeks, and risk management techniques for consistent profits.',
      instructor: { name: 'Amit Kumar', avatar: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop', bio: 'Options Trading Expert and Risk Management Specialist' },
      duration: '15 hours', lessons: 45, students: 890, rating: 4.9, reviews: 234, price: 6999, originalPrice: 9999,
      level: 'Advanced', category: 'Options Trading',
      image: 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
      featured: false, tags: ['Options', 'Greeks', 'Strategies', 'Risk Management'],
      chapters: [{ id: 'ch1', title: 'Options Fundamentals', pages: [{ id: 'p1', type: 'text', title: 'What are Options?', content: `<h1>Introduction to Options</h1><p>Options are financial derivatives that give you the right, but not the obligation, to buy or sell an underlying asset at a specific price within a certain time period.</p><h2>Types of Options</h2><ul><li><strong>Call Options:</strong> Right to buy at strike price</li><li><strong>Put Options:</strong> Right to sell at strike price</li></ul>` }] }]
    },
    {
      id: '4',
      title: 'Risk Management Essentials',
      description: 'Master the art of protecting your capital with proven risk management strategies and position sizing techniques.',
      instructor: { name: 'Neha Gupta', avatar: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop', bio: 'Risk Management Consultant and Portfolio Manager' },
      duration: '6 hours', lessons: 18, students: 1560, rating: 4.7, reviews: 423, price: 2999,
      level: 'Beginner', category: 'Risk Management',
      image: 'https://images.pexels.com/photos/6802042/pexels-photo-6802042.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
      featured: false, tags: ['Risk', 'Position Sizing', 'Stop Loss', 'Portfolio'],
      chapters: [{ id: 'ch1', title: 'Risk Fundamentals', pages: [{ id: 'p1', type: 'text', title: 'Understanding Risk', content: `<h1>What is Risk in Trading?</h1><p>Risk is the possibility of losing money on an investment.</p><h2>Types of Risk</h2><ul><li><strong>Market Risk:</strong> Overall market movements</li><li><strong>Specific Risk:</strong> Company-specific factors</li></ul>` }] }]
    }
  ];

  const getAllPages = (course: Course) => {
    const allPages: { page: Page; chapterIndex: number; pageIndex: number; chapterTitle: string }[] = [];
    course.chapters.forEach((chapter, chapterIndex) => {
      chapter.pages.forEach((page, pageIndex) => {
        allPages.push({ page, chapterIndex, pageIndex, chapterTitle: chapter.title });
      });
    });
    return allPages;
  };

  const getCurrentAbsolutePageIndex = (course: Course, chapterIndex: number, pageIndex: number) => {
    let absoluteIndex = 0;
    for (let i = 0; i < chapterIndex; i++) absoluteIndex += course.chapters[i].pages.length;
    return absoluteIndex + pageIndex;
  };

  const nextPage = () => {
    if (!selectedCourse) return;
    const allPages = getAllPages(selectedCourse);
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(selectedCourse, currentChapter, currentPage);
    if (currentAbsoluteIndex < allPages.length - 1) {
      const nextPageData = allPages[currentAbsoluteIndex + 1];
      setCurrentChapter(nextPageData.chapterIndex);
      setCurrentPage(nextPageData.pageIndex);
    }
  };

  const prevPage = () => {
    if (!selectedCourse) return;
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(selectedCourse, currentChapter, currentPage);
    if (currentAbsoluteIndex > 0) {
      const allPages = getAllPages(selectedCourse);
      const prevPageData = allPages[currentAbsoluteIndex - 1];
      setCurrentChapter(prevPageData.chapterIndex);
      setCurrentPage(prevPageData.pageIndex);
    }
  };

  const openCourse = (course: Course) => {
    setSelectedCourse(course);
    setCurrentChapter(0);
    setCurrentPage(0);
    setShowBookReader(true);
  };

  const closeCourse = () => {
    setShowBookReader(false);
    setSelectedCourse(null);
  };

  const levelColor = (level: string) => {
    if (level === 'Beginner') return 'bg-green-50 text-green-700 border-green-200';
    if (level === 'Intermediate') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  // Book Reader
  if (showBookReader && selectedCourse) {
    const allPages = getAllPages(selectedCourse);
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(selectedCourse, currentChapter, currentPage);
    const currentPageData = allPages[currentAbsoluteIndex];
    const nextPageData = currentAbsoluteIndex < allPages.length - 1 ? allPages[currentAbsoluteIndex + 1] : null;
    const isFirstPage = currentAbsoluteIndex === 0;
    const isLastPage = currentAbsoluteIndex === allPages.length - 1;

    return (
      <div className="fixed inset-0 bg-slate-100 z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={closeCourse} className="text-slate-400 hover:text-slate-700 transition-colors" aria-label="Close course reader">
                <X className="w-6 h-6" aria-hidden="true" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{selectedCourse.title}</h1>
                <p className="text-sm text-slate-500">{currentPageData.chapterTitle} · Page {currentAbsoluteIndex + 1} of {allPages.length}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-slate-500">Progress: {Math.round(((currentAbsoluteIndex + 1) / allPages.length) * 100)}%</div>
              <button className="text-slate-400 hover:text-amber-500 transition-colors">
                <Bookmark className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
          <div className="max-w-6xl w-full flex items-center justify-center relative h-full max-h-[600px]">
            <button
              onClick={prevPage} disabled={isFirstPage}
              className={`absolute left-0 z-10 p-3 rounded-full transition-all ${isFirstPage ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:scale-110 shadow-sm'}`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={nextPage} disabled={isLastPage}
              className={`absolute right-0 z-10 p-3 rounded-full transition-all ${isLastPage ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:scale-110 shadow-sm'}`}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Book Pages */}
            <div className="flex w-full max-w-5xl h-full shadow-2xl rounded-xl overflow-hidden border border-slate-200">
              {/* Left Page */}
              <div className="w-1/2 bg-white p-8 border-r border-slate-200 overflow-y-auto">
                <div className="h-full flex flex-col">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-2 font-serif">{currentPageData.page.title || currentPageData.chapterTitle}</h2>
                    <div className="w-10 h-1 bg-navy-500 rounded"></div>
                  </div>
                  <div className="flex-1 prose prose-slate max-w-none text-sm" dangerouslySetInnerHTML={{ __html: currentPageData.page.content }} />
                  <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">{currentPageData.chapterTitle} · Page {currentAbsoluteIndex + 1}</div>
                </div>
              </div>

              {/* Right Page */}
              <div className="w-1/2 bg-slate-50 p-8 overflow-y-auto">
                <div className="h-full flex flex-col">
                  {nextPageData ? (
                    <>
                      <div className="mb-6">
                        <h2 className="text-xl font-bold text-slate-900 mb-2 font-serif">{nextPageData.page.title || nextPageData.chapterTitle}</h2>
                        <div className="w-10 h-1 bg-slate-300 rounded"></div>
                      </div>
                      <div className="flex-1 prose prose-slate max-w-none text-sm" dangerouslySetInnerHTML={{ __html: nextPageData.page.content }} />
                      <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-400">{nextPageData.chapterTitle} · Page {currentAbsoluteIndex + 2}</div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col justify-center items-center text-center">
                      <div className="bg-green-50 border border-green-200 p-8 rounded-2xl mb-6">
                        <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-slate-900 mb-2 font-serif">Congratulations!</h3>
                        <p className="text-slate-600 text-sm">You've completed this course successfully.</p>
                      </div>
                      <div className="space-y-3 w-full">
                        <button className="w-full bg-navy-500 hover:bg-navy-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-sm">Get Certificate</button>
                        <button className="w-full border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-all text-sm">Browse More Courses</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white border-t border-slate-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {isLastPage ? 'Course Complete!' : `${currentPageData.chapterTitle} — ${currentPageData.page.title || 'Page ' + (currentAbsoluteIndex + 1)}`}
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-48 bg-slate-200 rounded-full h-1.5">
                <div className="bg-navy-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${((currentAbsoluteIndex + 1) / allPages.length) * 100}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-white text-slate-900">
        <div className="pt-16">
          {/* Category Filter */}
          <div className="bg-slate-50 border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-navy-500 text-white shadow-sm'
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-navy-300 hover:text-navy-600'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Featured Course */}
          {courses.find(c => c.featured) && selectedCategory === 'All' && (() => {
            const featuredCourse = courses.find(c => c.featured);
            if (!featuredCourse) return null;
            return (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-center space-x-2 mb-6">
                  <Award className="w-5 h-5 text-navy-500" />
                  <span className="text-navy-500 font-medium text-sm">Featured Course</span>
                </div>
                <div
                  className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-navy-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => openCourse(featuredCourse)}
                >
                  <div className="md:flex">
                    <div className="md:w-1/2">
                      <img src={featuredCourse.image} alt={featuredCourse.title} className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="md:w-1/2 p-8 md:p-12">
                      <span className="inline-block bg-navy-50 text-navy-600 border border-navy-100 px-3 py-1 rounded-full text-sm font-medium mb-4">{featuredCourse.category}</span>
                      <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight text-slate-900 font-serif group-hover:text-navy-500 transition-colors">{featuredCourse.title}</h2>
                      <p className="text-slate-600 mb-6 leading-relaxed text-sm">{featuredCourse.description}</p>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4 text-slate-500 text-sm">
                          <div className="flex items-center">
                            <img src={featuredCourse.instructor.avatar} alt={featuredCourse.instructor.name} className="w-6 h-6 rounded-full mr-2" />
                            {featuredCourse.instructor.name}
                          </div>
                          <div className="flex items-center"><Clock className="w-4 h-4 mr-1" />{featuredCourse.duration}</div>
                          <div className="flex items-center"><Users className="w-4 h-4 mr-1" />{featuredCourse.students}</div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-amber-400 fill-current" />
                          <span className="font-medium text-slate-900">{featuredCourse.rating}</span>
                          <span className="text-slate-400 text-sm">({featuredCourse.reviews})</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl font-bold text-green-600">₹{featuredCourse.price}</span>
                          {featuredCourse.originalPrice && <span className="text-slate-400 line-through text-sm">₹{featuredCourse.originalPrice}</span>}
                        </div>
                        <button className="bg-navy-500 hover:bg-navy-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-sm">Start Learning</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Courses Grid */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses
                .filter(course => selectedCategory === 'All' || course.category === selectedCategory)
                .filter(course => !course.featured)
                .map((course) => (
                  <div
                    key={course.id}
                    className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-navy-300 hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => openCourse(course)}
                  >
                    <div className="relative">
                      <img src={course.image} alt={course.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 text-navy-600 px-3 py-1 rounded-full text-xs font-medium border border-navy-100">{course.category}</span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${levelColor(course.level)}`}>{course.level}</span>
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-lg font-bold mb-2 leading-tight group-hover:text-navy-500 transition-colors text-slate-900 font-serif">{course.title}</h3>
                      <p className="text-slate-500 mb-4 leading-relaxed text-sm line-clamp-2">{course.description}</p>

                      <div className="flex items-center justify-between mb-4 text-slate-500 text-sm">
                        <div className="flex items-center">
                          <img src={course.instructor.avatar} alt={course.instructor.name} className="w-5 h-5 rounded-full mr-2" />
                          {course.instructor.name}
                        </div>
                        <div className="flex items-center"><Clock className="w-4 h-4 mr-1" />{course.duration}</div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">₹{course.price}</span>
                          {course.originalPrice && <span className="text-slate-400 line-through text-sm">₹{course.originalPrice}</span>}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-amber-400 fill-current" />
                          <span className="font-medium text-slate-900 text-sm">{course.rating}</span>
                          <span className="text-slate-400 text-xs">({course.reviews})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoursesPage;
